import { createFileRoute } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ClassDetailPage,
  Skeleton,
} from '@org/ui';
import type { UserSelect } from '@org/model';

export const Route = createFileRoute('/classes/$classId/student/$studentId')({
  component: TeacherStudentClassRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
    // 0-based block to open first (a homework notification deep-links here).
    block: parseBlockIdx(search.block),
  }),
});

function parseBlockIdx(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

function TeacherStudentClassRoute() {
  const { classId, studentId } = Route.useParams();
  const { block } = Route.useSearch();

  const {
    data: studentResponse,
    isLoading: isStudentLoading,
    isError,
  } = trpc.users.getUser.useQuery(studentId, {
    enabled: Boolean(studentId),
  });

  const student = Array.isArray(studentResponse)
    ? (studentResponse[0] as UserSelect | undefined)
    : undefined;

  return (
    <div className="space-y-6">
      <StudentContextBanner student={student} isLoading={isStudentLoading} hasError={isError} />
      <ClassDetailPage
        classId={classId}
        blockIdx={block}
        mode="teacher"
        viewerStudentId={studentId}
        isReadOnly
      />
    </div>
  );
}

function StudentContextBanner({
  student,
  isLoading,
  hasError,
}: {
  student?: UserSelect;
  isLoading: boolean;
  hasError: boolean;
}) {
  const displayName = formatStudentName(student);

  return (
    <div className="container mx-auto px-8 pt-6">
      <Card className="border-rose-100 bg-rose-50/70 shadow-sm">
        <CardHeader className="space-y-2">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-7 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          ) : hasError ? (
            <>
              <CardTitle className="text-xl text-rose-700">Não foi possível carregar a estudante.</CardTitle>
              <CardDescription className="text-rose-600">
                Verifique se o ID informado está correto ou tente novamente mais tarde.
              </CardDescription>
            </>
          ) : student ? (
            <>
              <Badge variant="outline" className="w-fit border-rose-200 bg-white text-rose-700">
                Visualização em modo leitura
              </Badge>
              <CardTitle className="text-2xl text-gray-900">{displayName}</CardTitle>
              <CardDescription className="text-gray-700">
                Você está visualizando a aula exatamente como {student.firstName ?? 'a estudante'} vê, mas sem opção de enviar respostas.
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-xl text-gray-900">Estudante não encontrada</CardTitle>
              <CardDescription className="text-gray-600">
                Não há registros para a aluna informada. Verifique o link utilizado.
              </CardDescription>
            </>
          )}
        </CardHeader>
      </Card>
    </div>
  );
}

function formatStudentName(student?: UserSelect) {
  if (!student) {
    return 'Estudante';
  }
  const name = `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim();
  return name || student.email || 'Estudante';
}
